import { useCallback, useEffect, useRef, useState } from "react"

import {
  useReportEnvironment,
  type ReportEnvironment,
} from "../../hooks/useReportEnvironment"
import {
  reportErrorText,
  ReportRequestError,
  saveNotebookRevision,
  unpackNotebook,
} from "../../utils/reportClient"
import {
  latestNotebookRevision,
  reportRevisionHistory,
} from "../../utils/reportRecipe"
import type { Notebook } from "../../utils/reportTypes"
import { RenameNotebookDialog } from "../RenameNotebookDialog"
import { HistoryPane } from "./HistoryPane"
import { LoadingDocument } from "./LoadingDocument"
import { NotebookDocument } from "./NotebookDocument"
import { NotebookPageHeader } from "./NotebookPageHeader"
import { ReportConversation } from "./ReportConversation"
import { ReportMessages } from "./ReportMessages"
import { reportClass } from "./ReportStyles"
import { ReportToolbarActions } from "./ReportToolbarActions"

/**
 * Read-only viewer for notebooks generated by the retired recipe builder.
 * The format can no longer be created, revised or scheduled; saved
 * notebooks stay readable, shareable, exportable and renameable.
 */
export function NotebookReports({
  organizationSlug,
  workspaceId,
  notebookId,
  revision,
  readonly = false,
  environment,
  pageHeader = false,
  onLocationChange,
}: {
  organizationSlug: string
  workspaceId: string
  notebookId?: string
  revision?: string
  readonly?: boolean
  environment?: ReportEnvironment
  /** Names the page in the app's top bar; isolated previews have no top bar. */
  pageHeader?: boolean
  /** Hears each URL the viewer writes for itself, before it is written. */
  onLocationChange?: (notebookId?: string, revision?: number) => void
}) {
  const { request, notebookUrl, updateLocation, sharingEnabled } =
    useReportEnvironment(organizationSlug, workspaceId, environment)
  const [notebook, setNotebook] = useState<Notebook | null>(null)
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [busy, setBusy] = useState("")
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [preview, setPreview] = useState(false)
  const notebookRef = useRef<Notebook | null>(null)
  const installedRequest = useRef<string | null>(null)
  const requestKey = JSON.stringify([
    organizationSlug,
    workspaceId,
    notebookId,
    revision || null,
  ])
  const latestRevision = useRef(0)
  const activeRequest = useRef<AbortController | null>(null)

  useEffect(() => () => activeRequest.current?.abort(), [])

  const installNotebook = useCallback(
    (loaded: Notebook, isPreview: boolean) => {
      notebookRef.current = loaded
      installedRequest.current = JSON.stringify([
        organizationSlug,
        workspaceId,
        loaded.id,
        isPreview ? String(loaded.revision) : null,
      ])
      setNotebook(loaded)
      setPreview(isPreview)
      latestRevision.current = latestNotebookRevision(loaded)
    },
    [organizationSlug, workspaceId],
  )

  useEffect(() => {
    if (!notebookId) {
      notebookRef.current = null
      installedRequest.current = null
      setNotebook(null)
      setPreview(false)
      return
    }
    if (installedRequest.current === requestKey) {
      return
    }
    const controller = new AbortController()
    activeRequest.current?.abort()
    activeRequest.current = controller
    notebookRef.current = null
    installedRequest.current = null
    setNotebook(null)
    setError("")
    setNotice("")
    setPreview(false)
    setBusy("Loading notebook")
    void (async () => {
      try {
        const loaded = unpackNotebook(
          await request<Notebook | { notebook: Notebook }>(
            `/api/notebooks/${encodeURIComponent(notebookId)}${
              revision ? `?revision=${encodeURIComponent(revision)}` : ""
            }`,
            undefined,
            "GET",
            controller.signal,
          ),
        )
        if (!controller.signal.aborted) {
          installNotebook(loaded, Boolean(revision))
        }
      } catch (failure: unknown) {
        if (!controller.signal.aborted) {
          setError(reportErrorText(failure))
        }
      } finally {
        if (!controller.signal.aborted) {
          setBusy("")
        }
      }
    })()
    return () => controller.abort()
  }, [installNotebook, loadAttempt, notebookId, request, requestKey, revision])

  const locked = Boolean(busy) || exporting

  const openNotebook = (id: string, nextRevision?: number) => {
    const controller = new AbortController()
    activeRequest.current?.abort()
    activeRequest.current = controller
    const signal = controller.signal
    setBusy("Loading notebook")
    return void (async () => {
      setError("")
      setNotice("")
      try {
        const loaded = unpackNotebook(
          await request<Notebook | { notebook: Notebook }>(
            `/api/notebooks/${encodeURIComponent(id)}${
              nextRevision
                ? `?revision=${encodeURIComponent(nextRevision)}`
                : ""
            }`,
            undefined,
            "GET",
            signal,
          ),
        )
        if (signal?.aborted) {
          return
        }
        installNotebook(loaded, nextRevision !== undefined)
        setShowHistory(false)
        updateLocation(
          { ...loaded, revision: nextRevision ?? loaded.revision } as Notebook,
          nextRevision,
        )
        onLocationChange?.(id, nextRevision)
      } catch (failure: unknown) {
        if (!signal.aborted) {
          setError(reportErrorText(failure))
        }
      } finally {
        if (!signal.aborted) {
          setBusy("")
        }
      }
    })()
  }

  const rename = async (title: string) => {
    const signal = activeRequest.current?.signal
    const current = notebookRef.current
    if (!current) {
      return
    }
    setBusy("Renaming notebook")
    try {
      const saved = await saveNotebookRevision(
        request,
        { ...current, title },
        latestRevision.current,
        signal,
      )
      if (!signal?.aborted) {
        installNotebook(saved, false)
        setNotice(`Renamed. Saved as revision ${saved.revision}.`)
      }
    } catch (failure: unknown) {
      if (signal?.aborted) {
        return
      }
      if (failure instanceof ReportRequestError && failure.status === 409) {
        const latest = await request<Notebook | { notebook: Notebook }>(
          `/api/notebooks/${encodeURIComponent(current.id)}`,
          undefined,
          "GET",
          signal,
        )
          .then(unpackNotebook)
          .catch(() => null)
        if (
          latest &&
          latestNotebookRevision(latest) > latestRevision.current &&
          !signal?.aborted
        ) {
          installNotebook(latest, false)
          throw new Error(
            `${reportErrorText(failure)} The latest revision is now open; save again to rename it.`,
          )
        }
      }
      throw failure
    } finally {
      if (!signal?.aborted) {
        setBusy("")
      }
    }
  }

  const share = () =>
    void (async () => {
      const current = notebookRef.current
      if (!current) {
        return
      }
      try {
        await navigator.clipboard.writeText(notebookUrl(current, true))
        setNotice(
          `Copied revision ${current.revision} link. It is available only to your signed-in account in this workspace.`,
        )
      } catch (failure: unknown) {
        setError(reportErrorText(failure))
      }
    })()

  return (
    <div
      className={reportClass(`root ${readonly ? "read-only" : ""}`)}
      data-testid="notebook-reports-page"
      data-covi-view="notebook-reports"
    >
      {pageHeader && (
        <NotebookPageHeader
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
          title={notebook?.title ?? null}
        />
      )}
      <RenameNotebookDialog
        open={renaming}
        title={notebook?.title ?? ""}
        onClose={() => setRenaming(false)}
        onRename={rename}
      />
      <div className={reportClass("main-workspace")}>
        <div className={reportClass("workspace-toolbar")}>
          <div className={reportClass("toolbar-left")}>
            <span>
              {preview || readonly
                ? `Read-only · revision ${notebook?.revision ?? "—"}`
                : notebook && (
                    <>Saved to Notebooks · revision {notebook.revision}</>
                  )}
            </span>
          </div>
          {notebook && (
            <ReportToolbarActions
              notebook={notebook}
              organizationSlug={organizationSlug}
              workspaceId={workspaceId}
              locked={locked}
              preview={preview}
              readonly={readonly}
              sharingEnabled={sharingEnabled}
              onExporting={setExporting}
              onHistory={() => setShowHistory(!showHistory)}
              onRename={() => setRenaming(true)}
              latestHref={preview ? notebookUrl(notebook, false) : undefined}
              onShare={share}
            />
          )}
        </div>
        <ReportMessages
          error={error}
          notice={notice}
          previewRevision={preview ? (notebook?.revision ?? null) : null}
          locked={locked}
          onDismissError={() => setError("")}
          onDismissNotice={() => setNotice("")}
          onLatest={() => notebook && openNotebook(notebook.id)}
          onRetryError={
            notebook
              ? undefined
              : () => {
                  setError("")
                  setLoadAttempt((attempt) => attempt + 1)
                }
          }
        />
        <div className={reportClass("content-layout")}>
          <div className={reportClass("document-pane")}>
            {busy && !notebook ? (
              <LoadingDocument action={busy} />
            ) : notebook ? (
              <>
                <fieldset
                  className={reportClass("document-fieldset")}
                  disabled={Boolean(busy)}
                >
                  <NotebookDocument notebook={notebook} />
                </fieldset>
                <ReportConversation
                  key={notebook.id}
                  turns={[...(notebook.chat_history ?? [])].sort((a, b) =>
                    a.created_at.localeCompare(b.created_at),
                  )}
                  busy=""
                />
              </>
            ) : null}
          </div>
          {showHistory && notebook && (
            <HistoryPane
              notebook={notebook}
              revisions={reportRevisionHistory(notebook)}
              busy={busy}
              onClose={() => setShowHistory(false)}
              openNotebook={openNotebook}
            />
          )}
        </div>
      </div>
    </div>
  )
}
